import { useCallback, useEffect, useRef, useState } from "react";

// Tiny silent WAV used to "unlock" audio playback on iOS within a user gesture.
const SILENT = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgD4AAAB9AAACABAAZGF0YQAAAAA=";

const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/mpeg",
  "audio/aac",
  "audio/ogg;codecs=opus",
  "audio/ogg",
];

function pickSupportedMime() {
  if (typeof MediaRecorder === "undefined") return "";
  const supported = MIME_CANDIDATES.filter(
    (t) => MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t)
  );
  // Prefer a supported type, else empty (browser default).
  return supported[0] || "";
}

function createRecorder(stream) {
  // isTypeSupported is unreliable on iOS, so actually attempt construction.
  const order = [];
  const picked = pickSupportedMime();
  if (picked) order.push(picked);
  MIME_CANDIDATES.forEach((m) => { if (!order.includes(m)) order.push(m); });
  for (const m of order) {
    try { return new MediaRecorder(stream, { mimeType: m }); } catch { /* next */ }
  }
  return new MediaRecorder(stream); // last resort: browser default
}

function extFromMime(mime) {
  if (!mime) return "webm";
  if (/mp4|aac|m4a/.test(mime)) return "mp4";
  if (mime.includes("mpeg")) return "mp3";
  if (mime.includes("ogg")) return "ogg";
  if (mime.includes("wav")) return "wav";
  return "webm";
}

/**
 * Voice engine: manages mic, recording, VAD auto-stop, level metering and TTS playback.
 * States: idle | requesting | listening | processing | speaking | error
 * onUtterance(blob, ext) is called when the user finishes an utterance.
 * onError(code) is called on failure. isSecureContext/support are surfaced.
 */
export function useVoiceEngine({ onUtterance, onError }) {
  const [phase, setPhase] = useState("idle");
  const [level, setLevel] = useState(0);
  const [continuous, setContinuous] = useState(true);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const mimeRef = useRef("audio/webm");
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const audioElRef = useRef(null);
  const unlockedRef = useRef(false);
  const lockRef = useRef(false); // prevents double init
  const speechSeenRef = useRef(false);
  const lastLoudRef = useRef(0);
  const startedAtRef = useRef(0);
  const phaseRef = useRef("idle");
  const endedRef = useRef(false);

  const setPhaseSafe = useCallback((p) => { phaseRef.current = p; setPhase(p); }, []);

  const isSupported =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices &&
    !!navigator.mediaDevices.getUserMedia &&
    typeof window !== "undefined" &&
    typeof window.MediaRecorder !== "undefined";

  const stopMeter = useCallback(() => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    setLevel(0);
  }, []);

  const releaseStream = useCallback(() => {
    try { streamRef.current?.getTracks().forEach((tr) => tr.stop()); } catch {}
    streamRef.current = null;
  }, []);

  const teardownAnalyser = useCallback(() => {
    stopMeter();
    try { analyserRef.current?.disconnect(); } catch {}
    analyserRef.current = null;
  }, [stopMeter]);

  // Build analyser + VAD/level loop on the active stream.
  const startMeter = useCallback((stream) => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      if (!audioCtxRef.current) audioCtxRef.current = new AC();
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      src.connect(analyser);
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.fftSize);
      speechSeenRef.current = false;
      lastLoudRef.current = performance.now();
      startedAtRef.current = performance.now();
      const tick = () => {
        if (phaseRef.current !== "listening" || !analyserRef.current) return;
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / data.length);
        setLevel(Math.min(1, rms * 4));
        const now = performance.now();
        const SPEECH = 0.02, SILENCE_MS = 1200, MIN_MS = 500, MAX_MS = 30000;
        if (rms > SPEECH) { speechSeenRef.current = true; lastLoudRef.current = now; }
        const elapsed = now - startedAtRef.current;
        const silentFor = now - lastLoudRef.current;
        if (elapsed > MIN_MS && speechSeenRef.current && silentFor > SILENCE_MS) { stop(); return; }
        if (elapsed > MAX_MS) { stop(); return; }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch { /* metering is best-effort */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Unlock <audio> playback + AudioContext during a user gesture (critical on iOS).
  const unlockAudio = useCallback(async () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC && !audioCtxRef.current) audioCtxRef.current = new AC();
      if (audioCtxRef.current?.state === "suspended") await audioCtxRef.current.resume().catch(() => {});
    } catch {}
    if (unlockedRef.current) return;
    try {
      const el = audioElRef.current;
      if (el) { el.muted = true; el.src = SILENT; await el.play().catch(() => {}); el.pause(); el.currentTime = 0; el.muted = false; }
      unlockedRef.current = true;
    } catch {}
  }, []);

  const start = useCallback(async () => {
    if (lockRef.current) return;
    if (phaseRef.current === "listening" || phaseRef.current === "processing") return;
    lockRef.current = true;
    endedRef.current = false;
    await unlockAudio();

    if (typeof window !== "undefined" && window.isSecureContext === false) {
      lockRef.current = false; setPhaseSafe("error"); onError?.("insecure"); return;
    }
    if (!isSupported) { lockRef.current = false; setPhaseSafe("error"); onError?.("unsupported"); return;
    }

    setPhaseSafe("requesting");
    let stream;
    const rich = { audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 }, video: false };
    try {
      stream = await navigator.mediaDevices.getUserMedia(rich);
    } catch (e1) {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      } catch (err) {
        lockRef.current = false;
        const name = err?.name || "";
        const msg = (err?.message || "").toLowerCase();
        if (msg.includes("permission") && msg.includes("policy")) onError?.("policy");
        else if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") onError?.("denied");
        else if (name === "NotFoundError" || name === "OverconstrainedError" || name === "DevicesNotFoundError") onError?.("notfound");
        else if (name === "NotReadableError" || name === "AbortError") onError?.("busy");
        else onError?.("generic:" + (name || "unknown"));
        setPhaseSafe("error");
        return;
      }
    }
    streamRef.current = stream;
    try {
      const mr = createRecorder(stream);
      mimeRef.current = mr.mimeType || "audio/webm";
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        teardownAnalyser();
        releaseStream();
        const blob = new Blob(chunksRef.current, { type: mimeRef.current });
        chunksRef.current = [];
        if (!blob.size) { setPhaseSafe(endedRef.current ? "idle" : "idle"); return; }
        setPhaseSafe("processing");
        try { await onUtterance?.(blob, extFromMime(mimeRef.current)); }
        catch { /* handled by caller */ setPhaseSafe("idle"); }
      };
      recorderRef.current = mr;
      mr.start();
      setPhaseSafe("listening");
      startMeter(stream);
    } catch (err) {
      releaseStream();
      lockRef.current = false;
      setPhaseSafe("error");
      onError?.("generic:" + (err?.name || "recorder"));
      return;
    }
    lockRef.current = false;
  }, [isSupported, onError, onUtterance, releaseStream, setPhaseSafe, startMeter, teardownAnalyser, unlockAudio]);

  const stop = useCallback(() => {
    stopMeter();
    const mr = recorderRef.current;
    if (mr && mr.state !== "inactive") {
      try { mr.stop(); } catch { teardownAnalyser(); releaseStream(); setPhaseSafe("idle"); }
    } else {
      teardownAnalyser(); releaseStream();
      if (phaseRef.current === "listening") setPhaseSafe("idle");
    }
  }, [releaseStream, setPhaseSafe, stopMeter, teardownAnalyser]);

  // Play a TTS answer (ArrayBuffer). Resolves when done; auto-resumes listening if continuous.
  const playAudio = useCallback((arrayBuffer, mime = "audio/mpeg") => {
    return new Promise((resolve) => {
      try {
        const url = URL.createObjectURL(new Blob([arrayBuffer], { type: mime }));
        const el = audioElRef.current;
        if (!el) { resolve(); return; }
        setPhaseSafe("speaking");
        el.src = url;
        const done = () => {
          URL.revokeObjectURL(url);
          el.onended = null; el.onerror = null;
          setPhaseSafe("idle");
          resolve();
          if (continuous && !endedRef.current) { setTimeout(() => start(), 250); }
        };
        el.onended = done;
        el.onerror = done;
        el.play().catch(() => done());
      } catch { setPhaseSafe("idle"); resolve(); }
    });
  }, [continuous, setPhaseSafe, start]);

  const stopSpeaking = useCallback(() => {
    try { const el = audioElRef.current; if (el) { el.pause(); el.onended = null; el.currentTime = 0; } } catch {}
  }, []);

  // Barge-in: interrupt playback and immediately start listening.
  const interrupt = useCallback(() => { stopSpeaking(); setPhaseSafe("idle"); start(); }, [setPhaseSafe, start, stopSpeaking]);

  const end = useCallback(() => {
    endedRef.current = true;
    stopMeter();
    stopSpeaking();
    try { if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop(); } catch {}
    recorderRef.current = null;
    teardownAnalyser();
    releaseStream();
    try { audioCtxRef.current?.suspend?.(); } catch {}
    lockRef.current = false;
    setPhaseSafe("idle");
  }, [releaseStream, setPhaseSafe, stopMeter, stopSpeaking, teardownAnalyser]);

  const toggle = useCallback(() => {
    const p = phaseRef.current;
    if (p === "listening") stop();
    else if (p === "speaking") interrupt();
    else if (p === "idle" || p === "error") start();
    // processing/requesting: ignore taps
  }, [interrupt, start, stop]);

  // Lifecycle: stop the mic if the page is hidden/backgrounded; never stay stuck listening.
  useEffect(() => {
    const onHide = () => { if (phaseRef.current === "listening") stop(); };
    const onVis = () => { if (document.visibilityState === "hidden" && phaseRef.current === "listening") stop(); };
    window.addEventListener("pagehide", onHide);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("pagehide", onHide);
      document.removeEventListener("visibilitychange", onVis);
      end();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { phase, level, continuous, setContinuous, isSupported, audioElRef, start, stop, end, toggle, interrupt, playAudio };
}
